"use client";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import * as React from "react";

import { CopyToClipboardAlert } from "@/components/alerts/alerts";
import { renderShareActionIcon } from "@/utils/share-page-buttons";

interface SharePageProps {
  text: string;
}

function SharePage({ text }: SharePageProps): React.JSX.Element {
  const [copyToClipboardAlertIsVisible, setCopyToClipboardAlertIsVisible] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const pathname = usePathname();

  useEffect(() => {
    const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://thefuturefound.com";
    setShareUrl(`${baseUrl}${pathname}`);
  }, [pathname]);

  const shareOptions = [
    {
      name: "Facebook",
      title: "Share on Facebook",
    },
    {
      name: "Twitter",
      title: "Share on Twitter",
    },
    {
      name: "LinkedIn",
      title: "Share on LinkedIn",
    },
    {
      name: "Email",
      title: "Email this page to a friend",
    },
    {
      name: "WhatsApp",
      title: "Share on WhatsApp",
    },
    {
      name: "sms",
      title: "Share via SMS",
    },
  ];

  const getGTMParamValue = ({
    gtmEvent,
    gtmParamName,
  }: {
    gtmEvent: string;
    gtmParamName: string;
  }): string => {
    const events: {
      share: {
        content_type: string;
        item_id: string;
      };
    } = {
      share: {
        content_type: "Webpage",
        item_id: shareUrl,
      },
    };

    return events[gtmEvent as keyof typeof events][gtmParamName as keyof typeof events.share];
  };

  const getShareUrl = ({
    platformName,
    textString,
  }: {
    platformName: string;
    textString: string;
  }): string => {
    const shareStringEncode = (str: string): string => encodeURIComponent(str);

    const shareUrlMap: Record<string, () => string> = {
      default: () => "#",
      email: () => {
        const subject: string = encodeURIComponent(textString);
        const body = encodeURIComponent(
          `${shareUrl}?utm_source=share&utm_campaign=website&utm_medium=email`,
        );
        return `mailto:?subject=${subject}&body=${body}`;
      },
      facebook: () =>
        `https://www.facebook.com/sharer/sharer.php?u=${shareStringEncode(`${shareUrl}`)}&quote=${shareStringEncode(textString)}`,
      linkedin: () =>
        `https://www.linkedin.com/sharing/share-offsite/?url=${shareStringEncode(`${shareUrl}`)}&title=${shareStringEncode(textString)}`,
      sms: () => {
        const message = `${textString} ${shareUrl}?utm_source=share&utm_campaign=website&utm_medium=sms`;
        return `sms:?body=${encodeURIComponent(message)}`;
      },
      twitter: () =>
        `https://twitter.com/intent/tweet?text=${shareStringEncode(textString)}&url=${shareStringEncode(`${shareUrl}&via=Info4PI`)}`,
      whatsapp: () => {
        const message = encodeURIComponent(
          `${textString} ${shareUrl}?utm_source=share&utm_campaign=website&utm_medium=whatsapp`,
        );
        return `whatsapp://send?text=${message}`;
      },
    };

    const shareFunction = shareUrlMap[platformName.toLowerCase()] || shareUrlMap.default;
    return shareFunction ? shareFunction() : "#";
  };

  return (
    <>
      <CopyToClipboardAlert
        dismiss={() => setCopyToClipboardAlertIsVisible(false)}
        isVisible={copyToClipboardAlertIsVisible}
        message="Link copied to clipboard."
      />
      <ul className="social share-buttons share-page" data-label="Share">
        {shareOptions.map((option) => (
          <li key={option.name}>
            <a
              className="gtm-share share-button"
              data-gtm-param-method={option.name}
              rel="noreferrer"
              target="_blank"
              title={option.title}
              data-gtm-param-content-type={getGTMParamValue({
                gtmEvent: "share",
                gtmParamName: "content_type",
              })}
              data-gtm-param-item-id={getGTMParamValue({
                gtmEvent: "share",
                gtmParamName: "item_id",
              })}
              href={getShareUrl({
                platformName: option.name,
                textString: text,
              })}
            >
              {renderShareActionIcon({
                iconName: option.name,
              })}
            </a>
          </li>
        ))}
        <li>
          <button
            className="gtm-share share-button"
            data-gtm-param-method="Copy to Clipboard"
            title="Copy Link"
            type="button"
            data-gtm-param-content-type={getGTMParamValue({
              gtmEvent: "share",
              gtmParamName: "content_type",
            })}
            data-gtm-param-item-id={getGTMParamValue({
              gtmEvent: "share",
              gtmParamName: "item_id",
            })}
            onClick={() => {
              const clipBoardAPIisAvailable = !!(
                navigator.clipboard && navigator.clipboard.writeText
              );

              if (clipBoardAPIisAvailable) {
                navigator.clipboard
                  .writeText(shareUrl)
                  .then(() => {
                    setCopyToClipboardAlertIsVisible(true);
                    setTimeout(() => {
                      setCopyToClipboardAlertIsVisible(false);
                    }, 5000);
                  })
                  .catch((error) => {
                    console.error("Error copying to clipboard:", error);
                  });
              } else {
                try {
                  const textArea = document.createElement("textarea");
                  textArea.value = shareUrl;
                  document.body.appendChild(textArea);
                  textArea.select();
                  // eslint-disable-next-line deprecation/deprecation
                  document.execCommand("copy");
                  document.body.removeChild(textArea);

                  setCopyToClipboardAlertIsVisible(true);
                  setTimeout(() => {
                    setCopyToClipboardAlertIsVisible(false);
                  }, 5000);
                } catch (error) {
                  console.error("Error copying to clipboard:", error);
                }
              }
            }}
          >
            {renderShareActionIcon({
              iconName: "copy",
            })}
          </button>
        </li>
      </ul>
    </>
  );
}

export default SharePage;
